<template>
    <div class="mol-view">
      <div class="page-header">
        <h2>발주서관리</h2>
        <div class="header-actions">
          <el-button type="primary" @click="addOrder">발주서 추가</el-button>
          <el-button @click="showHelp" size="small">
            <el-icon><QuestionFilled /></el-icon>
            도움말
          </el-button>
        </div>
      </div>
  
      <el-row :gutter="10" class="mb-3" align="middle">
        <el-col :span="10">
          <el-input
            v-model="searchQuery"
            placeholder="검색어 (스타일번호, 브랜드, 디자이너, 공급사, 입고처)"
            @input="applyFilters"
            clearable
          />
        </el-col>
        <el-col :span="6">
          <el-select v-model="selectedStatus" placeholder="상태" clearable @change="applyFilters">
            <el-option label="전체" value="" />
            <el-option label="작성중" value="DRAFT" />
            <el-option label="발주완료" value="ORDERED" />
            <el-option label="부분입고" value="PARTIAL_RECEIVED" />
            <el-option label="입고완료" value="FULLY_RECEIVED" />
            <el-option label="취소" value="CANCELLED" />
          </el-select>
        </el-col>
        <el-col :span="8">
          <el-button :type="showAll ? '' : 'warning'" @click="toggleShowAll" size="small">
            {{ showAll ? '스마트뷰' : '전체보기' }}
          </el-button>
        </el-col>
      </el-row>
  
      <div class="ag-grid-container">
        <ag-grid-vue
          class="ag-theme-alpine"
          style="width: 100%; height: 100%"
          :rowData="rowData"
          :columnDefs="columnDefs"
          :defaultColDef="defaultColDef"
          :rowSelection="'single'"
          :pagination="true"
          :paginationPageSize="50"
          @grid-ready="onGridReady"
          @rowClicked="onRowClicked"
        />
      </div>

      <HelpPanel v-model="helpVisible" :content-path="'screens/material-order-list'" />
    </div>
  </template>
  
  <script setup>
  import { ref, onMounted } from 'vue';
  import { useRouter } from 'vue-router';
  import { QuestionFilled } from '@element-plus/icons-vue'
  import axiosInstance from '@/api/axios';
  import HelpPanel from '@/components/help/HelpPanel.vue'
  import 'ag-grid-community/styles/ag-grid.css';
  import 'ag-grid-community/styles/ag-theme-alpine.css';
  
  const router = useRouter();
  
  const searchQuery = ref('');
  const selectedStatus = ref('');
  const rowData = ref([]);
  const showAll = ref(false);
  const helpVisible = ref(false);
  const showHelp = () => { helpVisible.value = true }
  
  // 컬럼 재구성 (2026-07-15): 가로 스크롤 없이 한눈에 —
  // 문서번호(PO-)·구분 한글화·핵심 컬럼만 고정폭, 스타일/공급사만 flex.
  // 작성자·작성일·입고처는 목록에서 제외 (상세에서 확인 — 검색은 입고처 여전히 지원)
  const columnDefs = ref([
    { field: 'document_number', headerName: '발주번호', width: 150, sort: 'desc',
      comparator: (a, b, nodeA, nodeB) => (nodeA.data?.id || 0) - (nodeB.data?.id || 0) },
    { field: 'company_name', headerName: '법인', width: 110 },
    {
      field: 'material_type_display', headerName: '구분', width: 80,
      cellStyle: (params) => ({
        color: params.value === '원자재' ? '#409EFF' : '#67C23A',
        fontWeight: 'bold',
      }),
    },
    { field: 'main_style_number', headerName: '스타일번호', flex: 1, minWidth: 120 },
    { field: 'brand_name', headerName: '브랜드', width: 95 },
    { field: 'designer_name', headerName: '디자이너', width: 90 },
    { field: 'supplier_name', headerName: '공급사', flex: 1, minWidth: 110 },
    { field: 'order_date', headerName: '발주일', width: 105 },
    { field: 'delivery_date', headerName: '납기일', width: 105 },
    { field: 'request_quantity', headerName: '수량', width: 85, type: 'rightAligned' },
    {
      field: 'status_display',
      headerName: '상태',
      width: 95,
      cellStyle: (params) => {
        const colors = {
          '작성중': '#909399', '발주완료': '#409EFF',
          '부분입고': '#E6A23C', '입고완료': '#67C23A', '취소': '#F56C6C',
        }
        return { color: colors[params.value] || '#303133', fontWeight: 'bold' }
      },
    },
    {
    field: 'is_auto', headerName: '입력', width: 72, sortable: true,
    cellRenderer: (params) => {
      if (!params.data) return ''
      if (params.value) {
        const o = params.data.auto_origin || {}
        const tip = `자동 변환 — 원본: ${o.source_file || ''}${o.sheet_name ? ' / ' + o.sheet_name : ''}`
        return `<span title="${tip}" style="padding:2px 8px;border-radius:4px;font-size:11px;color:white;background:#1f3a5f">자동</span>`
      }
      return '<span style="color:#909399;font-size:11px">직접</span>'
    },
  },
]);

  const defaultColDef = {
    resizable: true,
    sortable: true,
    filter: true,
  };


  const gridApi = ref(null);
  const columnApi = ref(null);

  const onGridReady = (params) => {
    gridApi.value = params.api;
    columnApi.value = params.columnApi;
    // 가로 스크롤 제거 — 컨테이너 폭에 맞춰 배분 (2026-07-15)
    params.api.sizeColumnsToFit();
    window.addEventListener('resize', () => {
      setTimeout(() => params.api.sizeColumnsToFit(), 100);
    });
  };
  
  const applyFilters = async () => {
    await fetchOrders({
      search: searchQuery.value,
      status: selectedStatus.value,
    });
  };

  const toggleShowAll = () => {
    showAll.value = !showAll.value;
    applyFilters();
  };
  
  const fetchOrders = async (filters = {}) => {
    try {
      // Remove empty filters
      Object.keys(filters).forEach((key) => {
        if (!filters[key]) {
          delete filters[key]
        }
      })

      // Smart filter: exclude FULLY_RECEIVED, CANCELLED when showAll is false
      if (!showAll.value && !filters.status) {
        filters.status__in = ['DRAFT', 'ORDERED', 'PARTIAL_RECEIVED']
      }

      const query = new URLSearchParams(filters).toString();
      const response = await axiosInstance.get(`/material-orders/?${query}`);
      rowData.value = response.data.results;
    } catch (err) {
      console.error('Error fetching material orders:', err);
    }
  };


  const onRowClicked = (event) => {
    router.push(`/material-orders/${event.data.id}`);
  };
  
  const addOrder = () => {
    router.push('/material-orders/new');
  };
  
  onMounted(async () => {
    await fetchOrders();
  });
  </script>
  
  <style scoped>
  .toolbar {
    margin-bottom: 10px;
    display: flex;
    justify-content: space-between;
  }
  
  /* ===== 이중 스크롤 방지 (PC) =====
     기존: domLayout="autoHeight"(콘텐츠만큼 그리드가 늘어남) + 고정 700px/calc 컨테이너가 충돌
     → 행이 많으면 페이지+그리드 이중 스크롤 (SupplierView 와 동일 패턴이었음).
     autoHeight 제거 후 .content(전역 스크롤러) 가시 영역을 flex 로 채운다. (2026-06-11) */
  @media (min-width: 769px) {
    .mol-view {
      height: 100%;
      display: flex;
      flex-direction: column;
    }
    .mol-view > .page-header,
    .mol-view .el-row {
      flex-shrink: 0;
    }
    .mol-view .ag-grid-container {
      width: 100%;
      flex: 1;
      min-height: 0;
    }
  }

  /* 모바일 폴백 */
  @media (max-width: 768px) {
    .ag-grid-container {
      width: 100%;
      height: calc(100vh - 260px);
    }
  }
  </style>
  